import { Button, CircularProgress, Divider, IconButton, makeStyles } from '@material-ui/core'
import { green } from '@material-ui/core/colors'
import { AccessTime, ArrowDownward, Block, Done, DoneAll, ExpandMore, GetApp, InsertDriveFile, Lock, PhoneMissed, Reply } from '@material-ui/icons'
import clsx from 'clsx'
import { format, isSameDay, parseISO } from 'date-fns'
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react'
import whatsBackgroundDark from '../../assets/wa-background-dark.png'
import whatsBackground from '../../assets/wa-background.png'
import { AuthContext } from '../../context/Auth/AuthContext'
import { ForwardMessageContext } from '../../context/ForwarMessage/ForwardMessageContext'
import { ReplyMessageContext } from '../../context/ReplyingMessage/ReplyingMessageContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import AudioMessageWhatsApp from '../AudioMessageWhatsApp'
import GifPreview from '../GifPreview'
import LocationPreview from '../LocationPreview'
import MarkdownWrapper from '../MarkdownWrapper'
import MessageOptionsMenu from '../MessageOptionsMenu'
import ModalImageCors from '../ModalImageCors'
import StickerPreview from '../StickerPreview'
import VCardPreview from '../VCardPreview'
import SelectMessageCheckbox from './SelectMessageCheckbox'
import { createPerfLogger, preloadImage } from '../../utils/perfLogger'

const useStyles = makeStyles(theme => ({
  messagesListWrapper: {
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
    minWidth: 300,
    minHeight: 200,
  },
  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '20px 20px 20px 20px',
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
  },
  circleLoading: {
    color: green[500],
    position: 'absolute',
    opacity: '70%',
    top: 0,
    left: '50%',
    marginTop: 12,
  },
  relationContainer: {
    marginTop: -10,
    paddingTop: 5,
    fontSize: '12px',
    color: '#666',
    display: 'block',
    textAlign: 'left',
  },
  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: 'auto',
    display: 'block',
    position: 'relative',
    '&:hover #messageActionsButton': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
    },
    whiteSpace: 'pre-wrap',
    backgroundColor: '#ffffff',
    color: '#303030',
    alignSelf: 'flex-start',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: '0 1px 1px #b3b3b3',
  },
  quotedContainerLeft: {
    margin: '-3px -80px 6px -6px',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    borderRadius: '7.5px',
    display: 'flex',
    position: 'relative',
  },
  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: 'auto',
    display: 'block',
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
  },
  quotedSideColorLeft: {
    flex: 'none',
    width: '4px',
    backgroundColor: '#6bcbef',
  },
  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: 'auto',
    display: 'block',
    position: 'relative',
    '&:hover #messageActionsButton': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
    },
    whiteSpace: 'pre-wrap',
    backgroundColor: '#dcf8c6',
    color: '#303030',
    alignSelf: 'flex-end',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: '0 1px 1px #b3b3b3',
  },
  messagePrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: 'auto',
    display: 'block',
    position: 'relative',
    '&:hover #messageActionsButton': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
    },
    whiteSpace: 'pre-wrap',
    backgroundColor: '#fff4c2',
    border: '1px solid #e6c95c',
    color: '#303030',
    alignSelf: 'flex-end',
    borderRadius: 8,
    borderBottomRightRadius: 0,
    padding: '5px 5px 0',
    boxShadow: '0 1px 1px #d6c48c',
  },
  privateMessageLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 80px 0 6px',
    fontSize: 12,
    fontWeight: 700,
    color: '#5c4300',
  },
  privateMessageIcon: {
    fontSize: 14,
  },
  messageSticker: {
    backgroundColor: 'transparent !important',
    boxShadow: 'none !important',
    padding: '0 !important',
  },
  quotedContainerRight: {
    margin: '-3px -80px 6px -6px',
    overflowY: 'hidden',
    backgroundColor: '#cfe9ba',
    borderRadius: '7.5px',
    display: 'flex',
    position: 'relative',
  },
  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: 'auto',
    whiteSpace: 'pre-wrap',
  },
  quotedSideColorRight: {
    flex: 'none',
    width: '4px',
    backgroundColor: '#35cd96',
  },
  messageActionsButton: {
    display: 'none',
    position: 'relative',
    color: '#999',
    zIndex: 1,
    backgroundColor: 'inherit',
    opacity: '90%',
    '&:hover, &.Mui-focusVisible': { backgroundColor: 'inherit' },
  },
  messageContactName: {
    display: 'flex',
    color: '#6bcbef',
    fontWeight: 500,
  },
  textContentItem: {
    overflowWrap: 'break-word',
    padding: '3px 80px 6px 6px',
  },
  textContentItemEdited: {
    overflowWrap: 'break-word',
    padding: '3px 120px 6px 6px',
    borderLeft: '3px solid #6bcbef',
  },
  textContentItemDeleted: {
    fontStyle: 'italic',
    color: 'rgba(0, 0, 0, 0.36)',
    overflowWrap: 'break-word',
    padding: '3px 80px 6px 6px',
  },
  forwardMessage: {
    fontSize: 12,
    fontStyle: 'italic',
    position: 'absolute',
    top: 0,
    left: 5,
    color: '#999',
    display: 'flex',
    alignItems: 'center',
  },
  messageSender: {
    display: 'block',
    marginTop: 4,
    paddingTop: 2,
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    fontSize: 11,
    fontStyle: 'italic',
    fontWeight: 'normal',
    color: '#999',
  },
  messageMedia: {
    objectFit: 'cover',
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  timestamp: {
    fontSize: 11,
    position: 'absolute',
    bottom: 0,
    right: 5,
    color: '#999',
  },
  reactionsBadge: {
    position: 'absolute',
    bottom: -10,
    left: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 999,
    padding: '1px 4px',
    fontSize: 12,
    boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
  },
  reactionsBadgeCount: {
    fontSize: 10,
    color: '#666',
    marginLeft: 1,
  },
  dailyTimestamp: {
    alignItems: 'center',
    textAlign: 'center',
    alignSelf: 'center',
    width: '110px',
    backgroundColor: '#e1f3fb',
    margin: '10px',
    borderRadius: '10px',
    boxShadow: '0 1px 1px #b3b3b3',
  },
  dailyTimestampText: {
    color: '#808888',
    padding: 8,
    alignSelf: 'center',
    marginLeft: '0px',
  },
  ticketTransition: {
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '18px 0 10px',
  },
  ticketTransitionLine: {
    flex: 1,
    height: 1,
    background: theme.mode === 'light' ? 'rgba(25, 88, 181, 0.16)' : 'rgba(255,255,255,0.16)',
  },
  ticketTransitionContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    minWidth: 260,
  },
  ticketTransitionChip: {
    backgroundColor: theme.mode === 'light' ? '#ffffffd9' : '#20262dd9',
    border: theme.mode === 'light' ? '1px solid rgba(25, 88, 181, 0.14)' : '1px solid rgba(255,255,255,0.1)',
    color: theme.palette.text.secondary,
    borderRadius: 999,
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    fontSize: 12,
    lineHeight: 1.4,
    padding: '6px 12px',
    textAlign: 'center',
  },
  ticketTransitionChipStrong: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  ackIcons: {
    fontSize: 18,
    verticalAlign: 'middle',
    marginLeft: 4,
  },
  deletedIcon: {
    fontSize: 18,
    verticalAlign: 'middle',
    marginRight: 4,
  },
  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: 'middle',
    marginLeft: 4,
  },
  downloadMedia: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    backgroundColor: theme.mode === 'light' ? '#1958B5' : '#1c1c1c',
    color: theme.mode === 'light' ? '#1958B5' : '#FFF',
  },
  documentContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: theme.mode === 'light' ? '#dcf8c6' : '#2a2a2a',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.mode === 'light' ? '#d4f0c0' : '#333333',
    },
  },
  documentContainerReceived: {
    backgroundColor: theme.mode === 'light' ? '#ffffff' : '#2a2a2a',
    '&:hover': {
      backgroundColor: theme.mode === 'light' ? '#f5f5f5' : '#333333',
    },
  },
  documentIcon: {
    fontSize: 40,
    color: theme.mode === 'light' ? '#0084ff' : '#4fc3f7',
    marginRight: 12,
  },
  documentInfo: {
    flex: 1,
    minWidth: 0,
  },
  documentName: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.mode === 'light' ? '#111b21' : '#e9edef',
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  documentMeta: {
    fontSize: '12px',
    color: theme.mode === 'light' ? '#667781' : '#8696a0',
  },
  documentDownload: {
    marginLeft: 8,
    color: theme.mode === 'light' ? '#667781' : '#8696a0',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      color: theme.mode === 'light' ? '#0084ff' : '#4fc3f7',
    },
    '& svg': {
      transition: 'opacity 0.3s ease',
    },
  },
  documentDownloadLoading: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      border: `2px solid ${theme.mode === 'light' ? '#0084ff' : '#4fc3f7'}`,
      borderTopColor: 'transparent',
      animation: '$spin 0.8s linear infinite',
      zIndex: 1,
    },
    '& svg': {
      opacity: 0,
      visibility: 'hidden',
    },
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    zIndex: 1000,
    transition: 'all 0.3s ease',
    opacity: 1,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
      transform: 'scale(1.1)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  scrollToBottomButtonHidden: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'scale(0.8)',
    '&:hover': {
      opacity: 0, // Garante que não apareça no hover quando escondido
    },
  },
}))

const reducer = (state, action) => {
  if (action.type === 'LOAD_MESSAGES') {
    const messages = action.payload
    const newMessages = []

    messages.forEach(message => {
      const messageIndex = state.findIndex(m => m.id === message.id)
      if (messageIndex !== -1) {
        state[messageIndex] = message
      } else {
        newMessages.push(message)
      }
    })

    return [...newMessages, ...state]
  }

  if (action.type === 'ADD_MESSAGE') {
    const newMessage = action.payload
    const messageIndex = state.findIndex(m => m.id === newMessage.id)

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage
    } else {
      state.push(newMessage)
    }

    return [...state]
  }

  if (action.type === 'UPDATE_MESSAGE') {
    const messageToUpdate = action.payload
    const messageIndex = state.findIndex(m => m.id === messageToUpdate.id)

    if (messageIndex !== -1) {
      if (messageToUpdate.isDeleted) {
        state[messageIndex] = {
          ...state[messageIndex],
          ...messageToUpdate,
        }
      } else {
        state[messageIndex] = {
          ...messageToUpdate,
          senderUser: messageToUpdate.senderUser || state[messageIndex].senderUser,
        }
      }
    }

    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }

  if (action.type === 'ADD_PENDING_MESSAGE') {
    const tempMessage = action.payload
    const exists = state.some(m => m.id === tempMessage.id || m.tempId === tempMessage.tempId)
    if (exists) return [...state]
    return [...state, tempMessage]
  }

  if (action.type === 'REPLACE_PENDING_MESSAGE') {
    const { tempId, message } = action.payload
    const existingIndex = message?.id ? state.findIndex(m => m.id === message.id) : -1
    const index = state.findIndex(m => m.id === tempId || m.tempId === tempId)

    if (existingIndex !== -1 && index !== -1 && existingIndex !== index) {
      state.splice(index, 1)
      return [...state]
    }

    if (index !== -1) {
      state[index] = message
      return [...state]
    }

    if (existingIndex !== -1) return [...state]
    return message ? [...state, message] : state
  }

  if (action.type === 'REMOVE_PENDING_MESSAGE') {
    const { tempId } = action.payload
    return state.filter(m => m.id !== tempId && m.tempId !== tempId)
  }

  return state
}

const MessagesList = ({ ticket, ticketId, isGroup, onlyTicket = false }) => {
  const classes = useStyles()
  const [messagesList, dispatch] = useReducer(reducer, [])
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const lastMessageRef = useRef()
  const [selectedMessage, setSelectedMessage] = useState({})
  const [anchorEl, setAnchorEl] = useState(null)
  const messageOptionsMenuOpen = Boolean(anchorEl)
  const currentTicketId = useRef(ticketId)
  // TODO non used
  // const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  const socketManager = useContext(SocketContext)
  const { user } = useContext(AuthContext)
  const { setReplyingMessage } = useContext(ReplyMessageContext)
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext)
  const [downloadingFiles, setDownloadingFiles] = useState(new Set())

  useEffect(() => {
    preloadImage(whatsBackground)
    preloadImage(whatsBackgroundDark)
  }, [])

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
    currentTicketId.current = ticketId
  }, [ticketId])

  useEffect(() => {
    setLoading(true)
    const perf = createPerfLogger('MessagesList.fetchMessages', { ticketId, pageNumber })
    const fetchMessages = async () => {
      if (ticketId === undefined) return
      try {
        const { data } = await api.get('/messages/' + ticketId, {
          params: onlyTicket ? { pageNumber, onlyTicket: true } : { pageNumber },
        })
        perf.mark('response-received', { returnedMessages: data.messages?.length || 0, hasMore: data.hasMore })

        if (currentTicketId.current === ticketId) {
          dispatch({ type: 'LOAD_MESSAGES', payload: data.messages })
          setHasMore(data.hasMore)
          setLoading(false)
        }

        if (pageNumber === 1 && data.messages.length > 1) {
          scrollToBottom()
        }
        perf.finish()
      } catch (err) {
        setLoading(false)
        toastError(err)
      }
    }
    fetchMessages()
  }, [pageNumber, ticketId, onlyTicket])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.getSocket(companyId)

    socket.on('ready', () => socket.emit('joinChatBox', `${ticket.id}`))

    socket.on(`company-${companyId}-appMessage`, data => {
      if (data.action === 'create' && data.message.ticketId === currentTicketId.current) {
        dispatch({ type: 'ADD_MESSAGE', payload: data.message })
        scrollToBottom()
      }

      if (data.action === 'update' && data.message.ticketId === currentTicketId.current) {
        if (data.message.isEdited) {
          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              ...data.message,
              body: data.message.body || 'Mensagem editada',
            },
          })
        } else {
          dispatch({ type: 'UPDATE_MESSAGE', payload: data.message })
        }
      }
    })

    return () => {
      socket.disconnect()
    }
  }, [ticketId, ticket, socketManager])

  useEffect(() => {
    const handlePendingMessage = event => {
      const { ticketId: pendingTicketId, message } = event.detail || {}
      if (pendingTicketId !== currentTicketId.current || !message) return
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: message })
      scrollToBottom()
    }

    const handleMessageSent = event => {
      const { ticketId: sentTicketId, tempId, message } = event.detail || {}
      if (sentTicketId !== currentTicketId.current || !tempId) return

      if (message) {
        dispatch({ type: 'REPLACE_PENDING_MESSAGE', payload: { tempId, message } })
      } else {
        dispatch({ type: 'REMOVE_PENDING_MESSAGE', payload: { tempId } })
      }
    }

    const handleMessageError = event => {
      const { ticketId: errorTicketId, tempId } = event.detail || {}
      if (errorTicketId !== currentTicketId.current || !tempId) return
      dispatch({ type: 'REMOVE_PENDING_MESSAGE', payload: { tempId } })
    }

    document.addEventListener('message-pending', handlePendingMessage)
    document.addEventListener('message-sent', handleMessageSent)
    document.addEventListener('message-error', handleMessageError)

    return () => {
      document.removeEventListener('message-pending', handlePendingMessage)
      document.removeEventListener('message-sent', handleMessageSent)
      document.removeEventListener('message-error', handleMessageError)
    }
  }, [])

  const loadMore = () => {
    setPageNumber(prevPageNumber => prevPageNumber + 1)
  }

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({})
    }
  }

  const handleScroll = e => {
    if (!hasMore) return
    const { scrollTop } = e.currentTarget

    if (scrollTop === 0) {
      document.getElementById('messagesList').scrollTop = 1
    }

    if (loading) {
      return
    }

    if (scrollTop < 50) {
      loadMore()
    }
  }

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null)
    setReplyingMessage(message)
  }

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget)
    setSelectedMessage(message)
  }

  const handleCloseMessageOptionsMenu = e => {
    setAnchorEl(null)
  }

  const formatFileSize = bytes => {
    if (!bytes) return ''
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const handleDownloadDocument = async (message, event) => {
    event.stopPropagation()
    if (!message.mediaUrl) return

    const messageId = message.id
    setDownloadingFiles(prev => new Set(prev).add(messageId))

    try {
      // Normalizar URL se necessário
      let downloadUrl = message.mediaUrl
      if (downloadUrl.startsWith('/')) {
        const baseURL = process.env.REACT_APP_BACKEND_URL || api.defaults.baseURL || 'http://localhost:3000'
        downloadUrl = `${baseURL}${downloadUrl}`
      }

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          Accept: '*/*',
        },
      })

      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo')
      }

      const blob = await response.blob()
      const fileName = getFileName(message)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error)
      toastError(error)
    } finally {
      setTimeout(() => {
        setDownloadingFiles(prev => {
          const newSet = new Set(prev)
          newSet.delete(messageId)
          return newSet
        })
      }, 500)
    }
  }

  const getFileExtension = filename => {
    if (!filename) return ''
    const parts = filename.split('.')
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : ''
  }

  const getFileName = message => {
    // Tenta extrair do dataJson primeiro (mensagens do Baileys)
    if (message.dataJson) {
      try {
        const data = JSON.parse(message.dataJson)
        const docMsg = data?.message?.documentMessage || data?.message?.documentWithCaptionMessage?.message?.documentMessage
        if (docMsg?.fileName) {
          return docMsg.fileName
        }
      } catch (e) {
        // Ignora erro de parsing
      }
    }

    if (message.body) {
      // Tenta extrair o nome do arquivo do body
      const lines = message.body.split('\n')
      for (let line of lines) {
        if (line.includes('.') && !line.startsWith('http') && !line.match(/^\d+\s*(B|KB|MB|GB)/i)) {
          return line.trim()
        }
      }
    }
    // Se não encontrar no body, tenta do mediaUrl
    if (message.mediaUrl) {
      const urlParts = message.mediaUrl.split('/')
      const fileName = urlParts[urlParts.length - 1]
      if (fileName && fileName.includes('.')) {
        // Remove timestamp se presente (formato: timestamp_filename.ext)
        const parts = fileName.split('_')
        if (parts.length > 1 && /^\d+$/.test(parts[0])) {
          return parts.slice(1).join('_')
        }
        return fileName
      }
    }
    return 'Documento'
  }

  const parseVcardBody = body => {
    if (!body) return { contact: '', number: '' }

    const lines = body
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
    const nameLine = lines.find(line => line.toUpperCase().startsWith('FN:'))
    const telLine = lines.find(line => /^ITEM\d+\.TEL|^TEL/i.test(line))

    const contact = nameLine ? nameLine.replace(/^FN:/i, '').trim() : ''

    let number = ''
    if (telLine) {
      const telParts = telLine.split(':')
      number = telParts[telParts.length - 1].trim()
    }

    return { contact, number }
  }

  const isVcardContent = msg => {
    if (!msg) return false
    const mediaType = msg.mediaType
    const body = typeof msg === 'string' ? msg : msg.body || ''
    if (['contactMessage', 'vcard'].includes(mediaType)) return true
    return typeof body === 'string' && body.toUpperCase().includes('BEGIN:VCARD')
  }

  const getMediaCaption = message => {
    if (!['image', 'video', 'gif'].includes(message.mediaType)) return null

    const body = (message.body || '').trim()
    const mediaPlaceholders = ['📷 Imagem', '🎥 Vídeo', '🎞️ GIF', '📎 Mídia', 'Imagem', 'Vídeo']
    const mediaFileName = decodeURIComponent((message.mediaUrl || '').split('/').pop() || '')

    if (!body || mediaPlaceholders.includes(body) || body === mediaFileName) return null

    return body
  }

  const checkMessageMedia = message => {
    if (message.mediaType === 'locationMessage' && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2) descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    } else if (message.mediaType === 'contactMessage' || message.mediaType === 'vcard' || isVcardContent(message)) {
      const { contact, number } = parseVcardBody(message.body)

      if (!contact && !number) return null

      return <VCardPreview contact={contact} numbers={number} />
    } else if (message.mediaType === 'sticker') {
      return <StickerPreview stickerUrl={message.mediaUrl} />
    } else if (message.mediaType === 'gif') {
      return <GifPreview gifUrl={message.mediaUrl} />
    } else if (message.mediaType === 'image') {
      return <ModalImageCors imageUrl={message.mediaUrl} />
    } else if (message.mediaType === 'audio') {
      return <AudioMessageWhatsApp url={message.mediaUrl} contact={!message.fromMe ? message.contact : null} fromMe={message.fromMe} />
    } else if (message.mediaType === 'video') {
      return <video className={classes.messageMedia} src={message.mediaUrl} controls />
    } else if (message.mediaType === 'document' || message.mediaType === 'application' || message.mediaType === 'documentMessage' || message.mediaType === 'documentWithCaptionMessage') {
      const fileName = getFileName(message)
      const fileExt = getFileExtension(fileName)

      // Tenta obter tamanho do arquivo do dataJson
      let fileSize = ''
      if (message.dataJson) {
        try {
          const data = JSON.parse(message.dataJson)
          const docMsg = data?.message?.documentMessage || data?.message?.documentWithCaptionMessage?.message?.documentMessage
          if (docMsg?.fileLength) {
            fileSize = formatFileSize(docMsg.fileLength)
          }
        } catch (e) {
          // Ignora erro de parsing
        }
      }

      // Se não encontrou no dataJson, tenta do body
      if (!fileSize && message.body) {
        const fileSizeMatch = message.body?.match(/(\d+)\s*(B|KB|MB|GB)/i)
        fileSize = fileSizeMatch ? fileSizeMatch[0] : ''
      }

      const isDownloading = downloadingFiles.has(message.id)

      return (
        <div className={`${classes.documentContainer} ${!message.fromMe ? classes.documentContainerReceived : ''}`}>
          <InsertDriveFile className={classes.documentIcon} />
          <div className={classes.documentInfo}>
            <div className={classes.documentName}>{fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName}</div>
            <div className={classes.documentMeta}>
              {fileExt ? `${fileExt} • ` : ''}
              {fileSize || 'Arquivo'}
            </div>
          </div>
          <div className={`${classes.documentDownload} ${isDownloading ? classes.documentDownloadLoading : ''}`} onClick={e => handleDownloadDocument(message, e)}>
            <ArrowDownward style={{ fontSize: 20 }} />
          </div>
        </div>
      )
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button startIcon={<GetApp />} variant='outlined' target='_blank' href={message.mediaUrl}>
              Download
            </Button>
          </div>
          <Divider />
        </>
      )
    }
  }

  const renderMessageSender = message => {
    const senderLabels = {
      user: `Enviado por ${message.senderUser?.name || 'um atendente'}`,
      bot: 'Enviado pelo bot',
      device: 'Enviado por outro dispositivo',
    }
    const senderLabel = senderLabels[message.senderType]

    if (!senderLabel) return null

    return <span className={classes.messageSender}>{senderLabel}</span>
  }

  const renderMessageAck = message => {
    if (message.ack === 0) {
      return <AccessTime fontSize='small' className={classes.ackIcons} />
    }
    if (message.ack === 1) {
      return <Done fontSize='small' className={classes.ackIcons} />
    }
    if (message.ack === 2) {
      return <Done fontSize='small' className={classes.ackIcons} />
    }
    if (message.ack === 3) {
      return <DoneAll fontSize='small' className={classes.ackIcons} />
    }
    if (message.ack === 4 || message.ack === 5) {
      return <DoneAll fontSize='small' className={classes.ackDoneAllIcon} style={{ color: '#0377FC' }} />
    }
  }

  const renderReactions = message => {
    if (!message.reactions || message.reactions.length === 0) return null

    const counts = new Map()
    message.reactions.forEach(({ type }) => counts.set(type, (counts.get(type) || 0) + 1))

    return (
      <div className={classes.reactionsBadge}>
        {Array.from(counts.entries()).map(([type, count]) => (
          <span key={type}>
            {type}
            {count > 1 && <span className={classes.reactionsBadgeCount}>{count}</span>}
          </span>
        ))}
      </div>
    )
  }

  const renderDailyTimestamps = (message, index) => {
    const shouldRenderTimestamp = index === 0 || !isSameDay(parseISO(messagesList[index].createdAt), parseISO(messagesList[index - 1].createdAt))
    const isLastMessage = index === messagesList.length - 1

    if (!shouldRenderTimestamp && !isLastMessage) return null

    return (
      <React.Fragment key={`timestamp-wrapper-${message.id}`}>
        {shouldRenderTimestamp && (
          <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>{format(parseISO(messagesList[index].createdAt), 'dd/MM/yyyy')}</div>
          </span>
        )}
        {isLastMessage && <div key={`ref-${message.createdAt}`} ref={lastMessageRef} style={{ float: 'left', clear: 'both' }} />}
      </React.Fragment>
    )
  }

  const renderNumberTicket = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageTicket = message.ticketId
      let previousMessageTicket = messagesList[index - 1].ticketId

      if (messageTicket !== previousMessageTicket) {
        return (
          <div className={classes.ticketTransition}>
            <div className={classes.ticketTransitionLine} />
            <div className={classes.ticketTransitionContent}>
              <div className={classes.ticketTransitionChip}>Conversa encerrada em {format(parseISO(messagesList[index - 1].createdAt), 'dd/MM/yyyy HH:mm')}</div>
              <div className={clsx(classes.ticketTransitionChip, classes.ticketTransitionChipStrong)}>Nova conversa iniciada em {format(parseISO(message.createdAt), 'dd/MM/yyyy HH:mm')}</div>
            </div>
            <div className={classes.ticketTransitionLine} />
          </div>
        )
      }
    }
  }

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe
      let previousMessageUser = messagesList[index - 1].fromMe

      if (messageUser !== previousMessageUser) {
        return <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
      }
    }
  }

  const renderQuotedMessage = message => {
    const quoted = message.quotedMsg || {}
    const isVcard = isVcardContent(quoted)
    const vcardData = isVcard ? parseVcardBody(quoted.body) : null

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        translate='no'
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: quoted?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg} translate='no'>
          {!quoted?.fromMe && <span className={classes.messageContactName}>{quoted?.contact?.name}</span>}

          {isVcard ? (
            <div>
              <div>
                <strong>Contato recebido</strong>
              </div>
              <div>{vcardData?.contact || 'Sem nome'}</div>
              <div>{vcardData?.number || 'Número não informado'}</div>
            </div>
          ) : (
            <>
              {quoted.mediaType === 'audio' && (
                <div className={classes.downloadMedia}>
                  <audio controls>
                    <source src={quoted.mediaUrl} type='audio/ogg'></source>
                  </audio>
                </div>
              )}
              {quoted.mediaType === 'video' && <video className={classes.messageMedia} src={quoted.mediaUrl} controls />}
              {quoted.mediaType === 'application' && (
                <div className={classes.downloadMedia}>
                  <Button
                    startIcon={<GetApp />}
                    variant='outlined'
                    href={quoted.mediaUrl}
                    target='_blank'
                    sx={theme => ({
                      borderColor: theme.palette.mode === 'light' ? '#1958B5' : '#FFF',
                      color: theme.palette.mode === 'light' ? '#1958B5' : '#FFF',
                      backgroundColor: theme.palette.mode === 'light' ? '#1958B510' : '#1c1c1c',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light' ? '#1958B520' : '#333',
                        borderColor: theme.palette.mode === 'light' ? '#1958B5' : '#FFF',
                        color: theme.palette.mode === 'light' ? '#1958B5' : '#FFF',
                      },
                    })}
                  >
                    Download
                  </Button>
                </div>
              )}
              {quoted.mediaType === 'gif' && <GifPreview gifUrl={quoted.mediaUrl} />}
              {quoted.mediaType === 'sticker' && <StickerPreview stickerUrl={quoted.mediaUrl} />}

              {quoted.mediaType === 'image' && <ModalImageCors imageUrl={quoted.mediaUrl} />}
              {(!quoted.mediaType ||
                (quoted.mediaType !== 'audio' &&
                  quoted.mediaType !== 'video' &&
                  quoted.mediaType !== 'application' &&
                  quoted.mediaType !== 'image' &&
                  quoted.mediaType !== 'gif' &&
                  quoted.mediaType !== 'sticker')) &&
                quoted?.body}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.mediaType === 'call_log') {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter} translate='no'>
                <IconButton
                  variant='contained'
                  size='small'
                  id='messageActionsButton'
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && <span className={classes.messageContactName}>{message.contact?.name}</span>}
                <div>
                  <PhoneMissed style={{ color: 'red', width: 20 }} /> <span>Chamada de voz/vídeo perdida às {format(parseISO(message.createdAt), 'HH:mm')}</span>
                </div>
              </div>
            </React.Fragment>
          )
        }

        if (message.isPrivate) {
          const canManagePrivateMessage = user?.profile === 'admin' || message.senderUserId === user?.id

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messagePrivate} translate='no'>
                {canManagePrivateMessage && !message.isDeleted && (
                  <IconButton
                    size='small'
                    id='messageActionsButton'
                    aria-label={i18n.t('messagesList.privateMessage.options')}
                    className={classes.messageActionsButton}
                    onClick={e => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
                <span className={classes.privateMessageLabel}>
                  <Lock className={classes.privateMessageIcon} />
                  {i18n.t('messagesList.privateMessage.label', { name: message.senderUser?.name || i18n.t('messagesList.privateMessage.unknownAuthor') })}
                </span>
                <div
                  className={clsx({
                    [classes.textContentItem]: !message.isEdited && !message.isDeleted,
                    [classes.textContentItemEdited]: message.isEdited && !message.isDeleted,
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                  translate='no'
                >
                  {message.isDeleted ? i18n.t('messagesList.privateMessage.deleted') : <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                  <span className={classes.timestamp}>
                    {message.isEdited ? 'Editada ' + format(parseISO(message.updatedAt || message.createdAt), 'HH:mm') : format(parseISO(message.createdAt), 'HH:mm')}
                  </span>
                </div>
              </div>
            </React.Fragment>
          )
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.messageSticker]: message.mediaType === 'sticker',
                })}
                style={message.reactions?.length > 0 ? { marginBottom: 14 } : undefined}
                title={message.queueId && message.queue?.name}
                onDoubleClick={e => hanldeReplyMessage(e, message)}
                translate='no'
              >
                {showSelectMessageCheckbox && <SelectMessageCheckbox message={message} />}
                <IconButton
                  variant='contained'
                  size='small'
                  id='messageActionsButton'
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}>
                      <Reply style={{ color: 'grey', transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && <span className={classes.messageContactName}>{message.contact?.name}</span>}

                {message.isDeleted && (
                  <div>
                    <span className={'message-deleted'}>
                      Essa mensagem foi apagada pelo contato &nbsp;
                      <Block color='error' fontSize='small' className={classes.deletedIcon} />
                    </span>
                  </div>
                )}

                {(message.mediaUrl ||
                  message.mediaType === 'locationMessage' ||
                  message.mediaType === 'vcard' ||
                  message.mediaType === 'contactMessage' ||
                  message.mediaType === 'document' ||
                  message.mediaType === 'application' ||
                  message.mediaType === 'documentMessage' ||
                  message.mediaType === 'documentWithCaptionMessage' ||
                  message.mediaType === 'sticker' ||
                  message.mediaType === 'gif' ||
                  message.mediaType === 'image' ||
                  message.mediaType === 'video' ||
                  message.mediaType === 'audio' ||
                  isVcardContent(message)) &&
                  checkMessageMedia(message)}
                <div className={message.isEdited ? classes.textContentItemEdited : classes.textContentItem} translate='no'>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== 'reactionMessage' &&
                  message.mediaType !== 'audio' &&
                  message.mediaType !== 'document' &&
                  message.mediaType !== 'application' &&
                  message.mediaType !== 'documentMessage' &&
                  message.mediaType !== 'documentWithCaptionMessage' &&
                  message.mediaType !== 'sticker' &&
                  message.mediaType !== 'image' &&
                  message.mediaType !== 'video' &&
                  message.mediaType !== 'gif' ? (
                    <MarkdownWrapper>{['locationMessage', 'contactMessage', 'vcard'].includes(message.mediaType) || isVcardContent(message) ? null : message.body}</MarkdownWrapper>
                  ) : null}
                  {getMediaCaption(message) && <MarkdownWrapper>{getMediaCaption(message)}</MarkdownWrapper>}
                  {message.quotedMsg && message.mediaType === 'reactionMessage' && (
                    <>
                      <span style={{ marginLeft: '0px' }}>
                        <MarkdownWrapper>{'' + message?.contact?.name + ' reagiu... ' + message.body}</MarkdownWrapper>
                      </span>
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? 'Editada ' + format(parseISO(message.updatedAt || message.createdAt), 'HH:mm') : format(parseISO(message.createdAt), 'HH:mm')}
                  </span>
                </div>
                {renderReactions(message)}
              </div>
            </React.Fragment>
          )
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.messageSticker]: message.mediaType === 'sticker',
                })}
                style={message.reactions?.length > 0 ? { marginBottom: 14 } : undefined}
                onDoubleClick={e => hanldeReplyMessage(e, message)}
                translate='no'
              >
                {showSelectMessageCheckbox && <SelectMessageCheckbox message={message} />}
                <IconButton
                  variant='contained'
                  size='small'
                  id='messageActionsButton'
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={e => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}>
                      <Reply style={{ color: 'grey', transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {(message.mediaUrl ||
                  message.mediaType === 'locationMessage' ||
                  message.mediaType === 'vcard' ||
                  message.mediaType === 'contactMessage' ||
                  message.mediaType === 'document' ||
                  message.mediaType === 'application' ||
                  message.mediaType === 'documentMessage' ||
                  message.mediaType === 'documentWithCaptionMessage' ||
                  message.mediaType === 'sticker' ||
                  message.mediaType === 'gif' ||
                  message.mediaType === 'image' ||
                  message.mediaType === 'video' ||
                  message.mediaType === 'audio' ||
                  isVcardContent(message)) &&
                  checkMessageMedia(message)}
                <div
                  className={clsx({
                    [classes.textContentItem]: !message.isEdited && !message.isDeleted,
                    [classes.textContentItemEdited]: message.isEdited,
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                  translate='no'
                >
                  {message.isDeleted && <Block color='disabled' fontSize='small' className={classes.deletedIcon} />}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== 'reactionMessage' &&
                    !['locationMessage', 'contactMessage', 'vcard'].includes(message.mediaType) &&
                    !isVcardContent(message) &&
                    message.mediaType !== 'audio' &&
                    message.mediaType !== 'document' &&
                    message.mediaType !== 'application' &&
                    message.mediaType !== 'documentMessage' &&
                    message.mediaType !== 'documentWithCaptionMessage' &&
                    message.mediaType !== 'sticker' &&
                    message.mediaType !== 'image' &&
                    message.mediaType !== 'video' &&
                    message.mediaType !== 'gif' && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                  {getMediaCaption(message) && <MarkdownWrapper>{getMediaCaption(message)}</MarkdownWrapper>}
                  {message.quotedMsg && message.mediaType === 'reactionMessage' && (
                    <>
                      <span style={{ marginLeft: '0px' }}>
                        <MarkdownWrapper>{'Você reagiu... ' + message.body}</MarkdownWrapper>
                      </span>
                    </>
                  )}
                  {renderMessageSender(message)}

                  <span className={classes.timestamp}>
                    {message.isEdited ? 'Editada ' + format(parseISO(message.updatedAt || message.createdAt), 'HH:mm') : format(parseISO(message.createdAt), 'HH:mm')}
                    {renderMessageAck(message)}
                  </span>
                </div>
                {renderReactions(message)}
              </div>
            </React.Fragment>
          )
        }
      })
      return viewMessagesList
    } else {
      return <div>Diga olá para seu novo contato!</div>
    }
  }

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu message={selectedMessage} anchorEl={anchorEl} menuOpen={messageOptionsMenuOpen} handleClose={handleCloseMessageOptionsMenu} />
      <div id='messagesList' className={classes.messagesList} onScroll={handleScroll}>
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  )
}

export default MessagesList
